import { useState } from 'react';
import { competencia as fmtCompetencia, moeda } from '../lib/formato.js';

export interface PontoSerie {
  competencia: string;
  emitidas: number;
  tomadas: number;
}

interface Props {
  pontos: PontoSerie[];
  /** O que está sendo medido, para o resumo acessível da série. */
  medida: string;
}

/**
 * Barras agrupadas por competência, em HTML e CSS — não em SVG. Um SVG que
 * estica para acompanhar a largura do container (`preserveAspectRatio="none"`)
 * distorce o texto junto com o vetor, e os rótulos de mês saem espremidos;
 * manter proporção deixaria o gráfico minúsculo com poucas competências. Com
 * caixas de layout o texto continua na escala da página em qualquer largura.
 *
 * Emitida e tomada ficam lado a lado, nunca empilhadas: empilhar somaria
 * receita com custo numa barra só, que é a leitura errada.
 */
export function SerieMensal({ pontos, medida }: Props) {
  const [foco, setFoco] = useState<number | null>(null);

  if (pontos.length === 0) return null;

  const maximo = Math.max(...pontos.map((p) => Math.max(p.emitidas, p.tomadas)), 1);
  const altura = (v: number) => (v <= 0 ? 0 : Math.max((v / maximo) * 100, 1.5));
  const ativo = foco === null ? null : pontos[foco];

  return (
    <div className="serie">
      <div className="serie-legenda">
        <span className="serie-chave"><i className="em" /> Emitidas</span>
        <span className="serie-chave"><i className="to" /> Tomadas</span>
        <span className="serie-foco">
          {ativo ? (
            <>
              <strong>{fmtCompetencia(ativo.competencia)}</strong>
              {' · emitidas '}{moeda(ativo.emitidas)}
              {' · tomadas '}{moeda(ativo.tomadas)}
            </>
          ) : (
            <span className="muted">Passe o ponteiro sobre um mês</span>
          )}
        </span>
      </div>

      <div
        className="serie-grade"
        role="img"
        aria-label={`${medida} por competência, de ${fmtCompetencia(pontos[0]!.competencia)} a ${fmtCompetencia(pontos[pontos.length - 1]!.competencia)}`}
        onMouseLeave={() => setFoco(null)}
      >
        {pontos.map((p, i) => (
          <div
            key={p.competencia}
            className={`serie-col${foco === i ? ' focada' : ''}`}
            onMouseEnter={() => setFoco(i)}
            title={`${fmtCompetencia(p.competencia)} — emitidas ${moeda(p.emitidas)}, tomadas ${moeda(p.tomadas)}`}
          >
            <div className="serie-pilha">
              <div className="serie-barra em" style={{ height: `${altura(p.emitidas)}%` }} />
              <div className="serie-barra to" style={{ height: `${altura(p.tomadas)}%` }} />
            </div>
            <div className="serie-mes">{p.competencia.slice(5)}</div>
            {i === 0 || p.competencia.endsWith('-01') ? (
              <div className="serie-ano">{p.competencia.slice(0, 4)}</div>
            ) : (
              <div className="serie-ano" aria-hidden="true">&nbsp;</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
